/**
 * LeavePolicies — entitlement / accrual rules per leave type.
 * Migrated to live API in Wave 3.2. Backend shape is slimmer than
 * the old mock: no annual_increment, max_entitlement, carry_over_expiry
 * — those were invented for the mock. Real fields are entitlement_days,
 * carry_over_max, accrual_method, probation_months, reset_month.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, DeleteOutlined, EditOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import type {
  LeavePolicy, LeavePolicyCreate, LeaveType,
} from '../../../api/attendance';
import { ConfirmDialog } from '../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

const EMPTY_FORM: LeavePolicyCreate = {
  leave_type_id: '',
  entitlement_days: 15,
  carry_over_max: 0,
  accrual_method: 'yearly',
  probation_months: 0,
  reset_month: 1,
};

const LeavePolicies: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Locale-aware "Jan 1" / "1月1日" / "1 ene" — uses the active i18n
  // language so the dropdown and table read naturally in every locale.
  const formatMonthDay1 = (monthIdx: number) =>
    new Date(2024, monthIdx, 1).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LeavePolicy | null>(null);
  const [form, setForm] = useState<LeavePolicyCreate>({ ...EMPTY_FORM });
  const [pendingDelete, setPendingDelete] = useState<LeavePolicy | null>(null);
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Diagnostic summary — populated alongside the list so admins
  // can see at a glance whether leave configuration is healthy.
  const [diagnose, setDiagnose] = useState<import('../../../api/attendance').LeaveDiagnoseResponse | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ps, ts, diag] = await Promise.all([
        attendanceApi.leavePoliciesApi.list(),
        attendanceApi.leaveTypesApi.list(),
        // diagnose is best-effort — failures shouldn't block the list
        attendanceApi.leaveBalancesApi.diagnose().catch(() => null),
      ]);
      setPolicies(ps);
      setTypes(ts);
      setDiagnose(diag);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:leavePolicies.loadFailed'));
      setPolicies([]);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const typeById = useMemo(() => {
    const m = new Map<string, LeaveType>();
    for (const t of types) m.set(t.id, t);
    return m;
  }, [types]);

  const filtered = useMemo(
    () =>
      policies.filter(p => {
        const name = typeById.get(p.leave_type_id)?.name ?? '';
        return name.toLowerCase().includes(filterText.toLowerCase());
      }),
    [policies, typeById, filterText],
  );
  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleOpen = (item?: LeavePolicy) => {
    if (item) {
      setEditing(item);
      setForm({
        leave_type_id: item.leave_type_id,
        entitlement_days: item.entitlement_days,
        carry_over_max: item.carry_over_max,
        accrual_method: item.accrual_method,
        probation_months: item.probation_months,
        reset_month: item.reset_month,
      });
    } else {
      setEditing(null);
      setForm({
        ...EMPTY_FORM,
        leave_type_id: types[0]?.id ?? '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.leave_type_id) return;
    setSaving(true);
    try {
      if (editing) {
        // Update endpoint takes a subset (no leave_type_id)
        await attendanceApi.leavePoliciesApi.update(editing.id, {
          entitlement_days: form.entitlement_days,
          carry_over_max: form.carry_over_max,
          accrual_method: form.accrual_method,
          probation_months: form.probation_months,
          reset_month: form.reset_month,
        });
        setToast(t('attendance:leavePolicies.toastUpdated'));
      } else {
        await attendanceApi.leavePoliciesApi.create(form);
        setToast(t('attendance:leavePolicies.toastCreated'));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.leavePoliciesApi.delete(pendingDelete.id);
      setToast(t('attendance:leavePolicies.toastDeleted'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.deleteFailed'));
      throw e;
    }
  };

  const accrualColor = (m: string): 'primary' | 'warning' | 'success' | 'default' => {
    if (m === 'monthly') return 'primary';
    if (m === 'yearly') return 'warning';
    if (m === 'immediate') return 'success';
    return 'default';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:leavePolicies.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:leavePolicies.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen()} disabled={types.length === 0}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('attendance:leavePolicies.addPolicy')}</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
      {!loading && types.length === 0 && (
        <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
          {t('attendance:leavePolicies.noTypesAlert')}
        </Alert>
      )}

      {/* Diagnostic summary — inline §13 stats pattern. One body2
          line with bold numbers + middot separators, plus a tiny
          warning row only when there's a real problem to fix. No
          card, no chips, no grid — matches Webhooks/API/TimeEntries. */}
      {diagnose && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{diagnose.active_workers}</strong> {t('attendance:leavePolicies.statsActiveWorkers')}
            {diagnose.invited_workers > 0 && (
              <> · <strong>{diagnose.invited_workers}</strong> {t('attendance:leavePolicies.statsInvited')}</>
            )}
            {' · '}<strong>{diagnose.leave_types}</strong> {t('attendance:leavePolicies.statsTypes')}
            {' · '}<strong>{diagnose.leave_policies}</strong> {t('attendance:leavePolicies.statsPolicies')}
            {' · '}<strong>{diagnose.leave_balance_rows}</strong>
            {' / '}{diagnose.expected_balance_rows} {t('attendance:leavePolicies.statsBalanceRows')}
            {' · '}
            <strong
              style={{
                color:
                  diagnose.coverage_pct >= 100 ? '#2e7d32'
                  : diagnose.coverage_pct > 0 ? '#ed6c02'
                  : '#9e9e9e',
              }}
            >
              {diagnose.expected_balance_rows > 0
                ? `${diagnose.coverage_pct}%`
                : '—'} {t('attendance:leavePolicies.statsCoverage')}
            </strong>
          </Typography>
          {diagnose.coverage_pct < 100 && diagnose.suggested_actions.length > 0 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}
            >
              {diagnose.suggested_actions[0]}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField label={t('attendance:leavePolicies.filterLabel')} value={filterText}
          onChange={e => { setFilterText(e.target.value); setPage(0); }}
          size="small" sx={{ width: 300 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filtered.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:leavePolicies.colLeaveType')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colEntitlement')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colCarryOver')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colAccrual')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colProbation')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colResets')}</TableCell>
                <TableCell align="center">{t('attendance:leavePolicies.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map(p => {
                const lt = typeById.get(p.leave_type_id);
                return (
                  <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(p)}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: lt?.color ?? 'grey.400', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{lt?.name ?? '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center"><Typography variant="caption" sx={{ fontWeight: 600 }}>{p.entitlement_days}d</Typography></TableCell>
                    <TableCell align="center">
                      {p.carry_over_max > 0 ? <Typography variant="caption" sx={{ fontWeight: 600 }}>{p.carry_over_max}d</Typography> : <Typography variant="caption" color="text.disabled">{t('attendance:leavePolicies.none')}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={p.accrual_method} size="small" color={accrualColor(p.accrual_method)} variant="outlined" sx={{ textTransform: 'capitalize', height: 20, fontSize: '0.7rem', borderRadius: 1 }} />
                    </TableCell>
                    <TableCell align="center"><Typography variant="caption">{p.probation_months > 0 ? t('attendance:leavePolicies.probationMonthsValue', { count: p.probation_months }) : t('attendance:leavePolicies.none')}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="caption">{p.reset_month >= 1 && p.reset_month <= 12 ? formatMonthDay1((p.reset_month - 1) % 12) : '—'}</Typography></TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); handleOpen(p); }}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); setPendingDelete(p); }} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginated.length === 0 && !loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">{filterText ? t('attendance:leavePolicies.noMatch') : t('attendance:leavePolicies.noDefined')}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ m: 0, py: 2, fontWeight: 600, fontSize: '1rem' }}>{editing ? t('attendance:leavePolicies.editTitle') : t('attendance:leavePolicies.addTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth disabled={!!editing}>
              <InputLabel>{t('attendance:leavePolicies.leaveTypeLabel')}</InputLabel>
              <Select value={form.leave_type_id} label={t('attendance:leavePolicies.leaveTypeLabel')} onChange={e => setForm(p => ({ ...p, leave_type_id: e.target.value }))}>
                {types.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <TextField label={t('attendance:leavePolicies.entitlementLabel')} type="number" value={form.entitlement_days}
                onChange={e => setForm(p => ({ ...p, entitlement_days: Number(e.target.value) }))}
                size="small" fullWidth inputProps={{ min: 0, step: 0.5 }} />
              <TextField label={t('attendance:leavePolicies.carryOverLabel')} type="number" value={form.carry_over_max ?? 0}
                onChange={e => setForm(p => ({ ...p, carry_over_max: Number(e.target.value) }))}
                size="small" fullWidth inputProps={{ min: 0, step: 0.5 }} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('attendance:leavePolicies.accrualLabel')}</InputLabel>
                <Select value={form.accrual_method ?? 'yearly'} label={t('attendance:leavePolicies.accrualLabel')}
                  onChange={e => setForm(p => ({ ...p, accrual_method: e.target.value as string }))}>
                  <MenuItem value="immediate">{t('attendance:leavePolicies.accrualImmediate')}</MenuItem>
                  <MenuItem value="monthly">{t('attendance:leavePolicies.accrualMonthly')}</MenuItem>
                  <MenuItem value="yearly">{t('attendance:leavePolicies.accrualYearly')}</MenuItem>
                </Select>
              </FormControl>
              <TextField label={t('attendance:leavePolicies.probationLabel')} type="number" value={form.probation_months ?? 0}
                onChange={e => setForm(p => ({ ...p, probation_months: Number(e.target.value) }))}
                size="small" fullWidth inputProps={{ min: 0, step: 1 }} />
            </Stack>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('attendance:leavePolicies.resetMonthLabel')}</InputLabel>
              <Select value={form.reset_month ?? 1} label={t('attendance:leavePolicies.resetMonthLabel')}
                onChange={e => setForm(p => ({ ...p, reset_month: Number(e.target.value) }))}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i} value={i + 1}>{formatMonthDay1(i)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} size="small" disabled={!form.leave_type_id || saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {saving ? <CircularProgress size={18} /> : editing ? t('attendance:leavePolicies.saveChanges') : t('attendance:leavePolicies.addPolicy')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:leavePolicies.deleteConfirmTitle')}
        description={t('attendance:leavePolicies.deleteConfirmDescription')}
        subject={pendingDelete && (
          <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {typeById.get(pendingDelete.leave_type_id)?.name ?? '—'}
          </Box>
        )}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeavePolicies;
